import { createAsyncThunk } from "@reduxjs/toolkit";
import { fetchLoyaltyStatus, loyaltySlice } from "@store/slices/loyaltySlice";
import type { RootStoreState } from "@store/types";
import {
  clearFreeTrialRedirect,
  deleteAnonymousCart,
  getAnonymousCartId,
  setAnonymousCartId,
} from "@store/utils/cartHelpers";

import type { CartType } from "@jti/fe-common";
import { getCurrentMagentoClient } from "@jti/magento";
import { getUser } from "@jti/rest-api";

import { addFreeTrialProductToCart } from "../addProductToCart";
import { fetchCleanCartItems, fetchTrialCartItems } from "../fetchCartItems";

interface ProductInterface {
  sku: string;
  bundle?: any;
  requestId?: string;
}

type FetchCartPayloadType = {
  useAnonymous?: boolean;
  freeTrialProduct?: ProductInterface;
};

const getAnonymousCart = async (useAnonymous: boolean, isFreeTrial: boolean, storeName: string) => {
  if (!isFreeTrial) {
    const magentoClient = getCurrentMagentoClient(isFreeTrial);
    const anonymousCartId = getAnonymousCartId(storeName, isFreeTrial);

    if (useAnonymous && anonymousCartId && !(await magentoClient.checkCartAvailability(anonymousCartId))) {
      deleteAnonymousCart(storeName, isFreeTrial);

      return null;
    }

    return anonymousCartId;
  }

  return null;
};

const getFreeTrialCart = async (
  freeTrialProduct: ProductInterface | undefined,
  isCustomerCartExists: boolean,
  id: string
) => {
  const magentoClient = getCurrentMagentoClient(true);

  if (freeTrialProduct) {
    if (isCustomerCartExists) {
      const { items } = await magentoClient.getCartItems(id);

      if (items) {
        await magentoClient.removeItemsFromCart(
          id,
          items.map((item) => item.id!)
        );
      }
    }

    return addFreeTrialProductToCart({
      ...freeTrialProduct,
      cartId: id,
    });
  }

  clearFreeTrialRedirect();
  return fetchTrialCartItems(id);
};

const fetchCartDeclaration = (isFreeTrial: boolean) =>
  createAsyncThunk<
    CartType,
    FetchCartPayloadType,
    {
      state: RootStoreState;
    }
  >(
    isFreeTrial ? "freeTrialCart/fetchCart" : "cart/fetchCart",
    async ({ useAnonymous, freeTrialProduct }: FetchCartPayloadType, { dispatch, getState }) => {
      const storeName = isFreeTrial ? "" : getState().cart.storeConfig.store_group_code;
      const user = getUser();
      const magentoClient = getCurrentMagentoClient(isFreeTrial);
      const anonymousCartId = await getAnonymousCart(!!useAnonymous, isFreeTrial, storeName);
      const isUserFlow = !!user.magento_token;
      const isEmptyCartFlow = useAnonymous && !anonymousCartId;

      let isCustomerCartExists = false;
      let loyaltyStatus;
      let currentCart: CartType = {
        id: anonymousCartId,
        can_apply_coupons: true,
      };

      if (!getState().loyalty.isLoaded) {
        await dispatch(fetchLoyaltyStatus());
        loyaltyStatus = getState().loyalty.status;
      }
      if (isUserFlow) {
        currentCart = await magentoClient.getCustomerCart();
        isCustomerCartExists = true;
        if (loyaltyStatus && !isFreeTrial) {
          const loyaltyCampaignId = currentCart?.applied_campaign?.loyalty_discount_uuid;
          if (loyaltyCampaignId) {
            dispatch(loyaltySlice.actions.setLoyaltyCampaignId(loyaltyCampaignId));
          }
        }
      } else if (isEmptyCartFlow) {
        currentCart = await magentoClient.createEmptyCart();
      }

      if (isUserFlow && anonymousCartId && useAnonymous && currentCart.id && !isFreeTrial) {
        await magentoClient.mergeCarts(anonymousCartId, currentCart.id);
        dispatch(fetchCleanCartItems(currentCart.id!));
        deleteAnonymousCart(storeName, isFreeTrial);
      } else if (isEmptyCartFlow && !isFreeTrial) {
        setAnonymousCartId(currentCart.id!, storeName, isFreeTrial);
      }

      if (isFreeTrial) {
        const action: any = await getFreeTrialCart(freeTrialProduct, isCustomerCartExists, currentCart.id!);
        dispatch(action);
      }

      return currentCart;
    }
  );

export const fetchCart = fetchCartDeclaration(false);
export const fetchTrialCart = fetchCartDeclaration(true);
